/**
 * Roma Kitchen menu seed data.
 *
 * Source of truth: owner-provided PDF menus:
 * - "Roma Kitchen Menu.pdf" (food: Antipasti, Insalata, La Pasta, Secondi Primi, Sides)
 * - "West Temp.pdf" (drinks: Cocktails, Wine List, Beers, Beverages, Desserts)
 * - "chefs-roma-kitchen-banquet-menu-polished 2.pdf" (catering trays, 2026-07-03)
 *
 * Photo URLs originally captured from the live SpotHopper menu
 * (https://chefsromakitchen.com), refreshed 2026-06-01.
 *
 * Sort order blocks: dinner food 1-58, catering 101-139, drinks 201-282.
 */

export type RomaMenuSeedCategory =
  | "starters"
  | "salads"
  | "pastas"
  | "mains"
  | "seafood"
  | "desserts"
  | "sides"
  | "catering_trays"
  | "cocktails"
  | "wine"
  | "beer"
  | "beverages"
  | "energy_drinks";

export type RomaMenuSeedSection = "dinner" | "catering" | "drinks";

export type RomaMenuSeedItem = {
  name: string;
  description: string;
  price: number;
  priceText: string;
  fullPrice?: number;
  fullPriceText?: string;
  category: RomaMenuSeedCategory;
  section: RomaMenuSeedSection;
  originalSection: string;
  photoUrl?: string;
  isPopular: boolean;
  sortOrder: number;
};

export const ROMA_MENU_ITEMS: RomaMenuSeedItem[] = [
  // ── Antipasti ────────────────────────────────────────────────────────────
  {
    name: "Bread & Butter",
    description: "Add garlic or cheese & marinara for an additional charge.",
    price: 3.5,
    priceText: "$3.50",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/01-bread-and-butter.jpg",
    isPopular: false,
    sortOrder: 1,
  },
  {
    name: "Warm Olives",
    description: "Olive oil, garlic, and red chili flakes.",
    price: 10,
    priceText: "$10.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/02-warm-olives.jpg",
    isPopular: false,
    sortOrder: 2,
  },
  {
    name: "Bruschetta",
    description: "Marinated tomatoes and basil.",
    price: 16,
    priceText: "$16.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/03-bruschetta.jpg",
    isPopular: true,
    sortOrder: 3,
  },
  {
    name: "Roasted Red Peppers and Burrata",
    description: "Olive oil and balsamic reduction. Add prosciutto for an additional charge.",
    price: 16,
    priceText: "$16.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    isPopular: false,
    sortOrder: 4,
  },
  {
    name: "Stuffed Artichokes",
    description: "Bread crumbs, olive oil, and parmesan.",
    price: 16,
    priceText: "$16.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/36-stuffed-artichokes.jpg",
    isPopular: true,
    sortOrder: 5,
  },
  {
    name: "Crostini",
    description: "Prosciutto, arugula, and burrata.",
    price: 16,
    priceText: "$16.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/07-crostini.jpg",
    isPopular: false,
    sortOrder: 6,
  },
  {
    name: "Meatballs",
    description: "Served with pomodoro.",
    price: 12,
    priceText: "$12.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/35-meatballs.jpg",
    isPopular: true,
    sortOrder: 7,
  },
  {
    name: "Bowl of Clams",
    description: "Red or white.",
    price: 25,
    priceText: "$25.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/04-bowl-of-clams.jpg",
    isPopular: false,
    sortOrder: 8,
  },
  {
    name: "Cioppino",
    description: "Clams, scallops, salmon, shrimp, tomato, and lobster broth.",
    price: 30,
    priceText: "$30.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    isPopular: false,
    sortOrder: 9,
  },
  {
    name: "Fried Calamari",
    description: "Served with spicy pomodoro.",
    price: 19,
    priceText: "$19.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/05-fried-calamari.jpg",
    isPopular: false,
    sortOrder: 10,
  },
  {
    name: "Shrimp Scampi",
    description: "Butter, white wine, and smoked paprika.",
    price: 18,
    priceText: "$18.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/06-shrimp-scampi.jpg",
    isPopular: false,
    sortOrder: 11,
  },
  {
    name: "Beef Carpaccio",
    description: "Arugula, parmesan, tomatoes, mustard, and fried capers.",
    price: 18,
    priceText: "$18.00",
    category: "starters",
    section: "dinner",
    originalSection: "Antipasti",
    isPopular: false,
    sortOrder: 12,
  },
  // ── Insalata ─────────────────────────────────────────────────────────────
  {
    name: "Caesar Salad",
    description:
      "Romaine, parmigiano, and house dressing. Add chicken, salmon, or shrimp for an additional charge.",
    price: 17,
    priceText: "$17.00",
    category: "salads",
    section: "dinner",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/caesar-salad.jpg",
    isPopular: true,
    sortOrder: 13,
  },
  {
    name: "Caprese Salad",
    description:
      "Burrata, heirloom tomatoes, olive oil, basil, and balsamic. Add prosciutto, chicken, salmon, or shrimp for an additional charge.",
    price: 17,
    priceText: "$17.00",
    category: "salads",
    section: "dinner",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/08-caprese-salad.jpg",
    isPopular: true,
    sortOrder: 14,
  },
  {
    name: "Avocado and Bacon Salad",
    description:
      "Field greens, tomatoes, cucumbers, and vinaigrette. Add chicken, salmon, or shrimp for an additional charge.",
    price: 16,
    priceText: "$16.00",
    category: "salads",
    section: "dinner",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/09-avocado-and-bacon-salad.jpg",
    isPopular: false,
    sortOrder: 15,
  },
  {
    name: "Grilled Vegetable Salad",
    description:
      "Mixed greens, tomatoes, feta, and vinaigrette. Add chicken, salmon, or shrimp for an additional charge.",
    price: 18,
    priceText: "$18.00",
    category: "salads",
    section: "dinner",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/10-grilled-vegetable-salad.jpg",
    isPopular: false,
    sortOrder: 16,
  },
  {
    name: "Mixed Greens Salad",
    description:
      "Olive oil, balsamic, cucumbers, tomatoes, and red onion. Add chicken, salmon, or shrimp for an additional charge.",
    price: 18,
    priceText: "$18.00",
    category: "salads",
    section: "dinner",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/11-mixed-greens-salad.jpg",
    isPopular: false,
    sortOrder: 17,
  },
  // ── La Pasta ─────────────────────────────────────────────────────────────
  {
    name: "Penne Bolognese",
    description: "A family favorite.",
    price: 29,
    priceText: "$29.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/12-penne-bolognese.jpg",
    isPopular: true,
    sortOrder: 18,
  },
  {
    name: "Tortellini",
    description: "Prosciutto, peas, cremini, and pink cream sauce.",
    price: 28,
    priceText: "$28.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/13-tortellini.jpg",
    isPopular: true,
    sortOrder: 19,
  },
  {
    name: "Meat Lasagna",
    description: "Fresh mozzarella and meat sauce.",
    price: 29,
    priceText: "$29.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/16-meat-lasagna.jpg",
    isPopular: false,
    sortOrder: 20,
  },
  {
    name: "Spaghetti and Meatballs",
    description: "Pomodoro sauce.",
    price: 27,
    priceText: "$27.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/20-spaghetti-and-meatballs.jpg",
    isPopular: true,
    sortOrder: 21,
  },
  {
    name: "Linguine with Fresh Clams",
    description: "Red or white. Add shrimp for an additional charge.",
    price: 30,
    priceText: "$30.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/15-linguine-with-fresh-clams.jpg",
    isPopular: false,
    sortOrder: 22,
  },
  {
    name: "Gnocchi",
    description: "Cream sauce, truffles, and mushrooms.",
    price: 29,
    priceText: "$29.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/14-gnocchi.jpg",
    isPopular: true,
    sortOrder: 23,
  },
  {
    name: "Spaghetti Cacio e Pepe",
    description: "Fresh cracked pepper and parmigiano.",
    price: 22,
    priceText: "$22.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/17-spaghetti-cacio-e-pepe.jpg",
    isPopular: false,
    sortOrder: 24,
  },
  {
    name: "Pappardelle Alfredo",
    description:
      "Fresh cream, butter, and parmigiano. Add chicken, shrimp, or salmon for an additional charge.",
    price: 25,
    priceText: "$25.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/18-pappardelle-alfredo.jpg",
    isPopular: false,
    sortOrder: 25,
  },
  {
    name: "Rigatoni Puttanesca",
    description: "Kalamata olives, garlic, capers, and spicy marinara.",
    price: 24,
    priceText: "$24.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/19-rigatoni-puttanesca.jpg",
    isPopular: false,
    sortOrder: 26,
  },
  {
    name: "Shrimp Diablo",
    description: "Spicy pomodoro and pasta.",
    price: 30,
    priceText: "$30.00",
    category: "pastas",
    section: "dinner",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/21-shrimp-diablo.jpg",
    isPopular: false,
    sortOrder: 27,
  },
  // ── Secondi Primi ────────────────────────────────────────────────────────
  {
    name: "Chicken Parmigiana",
    description:
      "Mozzarella and pomodoro. Served with choice of penne pasta or vegetables and potatoes.",
    price: 30,
    priceText: "$30.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/22-chicken-parmigiana.jpg",
    isPopular: true,
    sortOrder: 28,
  },
  {
    name: "Chicken Milanese",
    description: "Tomato, parmesan, and arugula. Excludes sides.",
    price: 28,
    priceText: "$28.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/23-chicken-milanese.jpg",
    isPopular: false,
    sortOrder: 29,
  },
  {
    name: "Prime New York Strip",
    description: "12 oz. Served with choice of penne pasta or vegetables and potatoes.",
    price: 49,
    priceText: "$49.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/30-prime-new-york-strip.jpg",
    isPopular: false,
    sortOrder: 30,
  },
  {
    name: "Duroc Pork Chops",
    description:
      "Cherry peppers and onions. Served with choice of penne pasta or vegetables and potatoes.",
    price: 38,
    priceText: "$38.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/31-duroc-pork-chops.jpg",
    isPopular: false,
    sortOrder: 31,
  },
  {
    name: "New Zealand Lamb",
    description: "Full rack. Served with choice of penne pasta or vegetables and potatoes.",
    price: 50,
    priceText: "$50.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/roma-kitchen-hero-lamb-chops.jpg",
    isPopular: false,
    sortOrder: 32,
  },
  {
    name: "Chicken Piccata",
    description: "Spaghetti, butter, white wine, and capers. Excludes sides.",
    price: 30,
    priceText: "$30.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/24-chicken-piccata.jpg",
    isPopular: false,
    sortOrder: 33,
  },
  {
    name: "Veal Parmigiana",
    description:
      "Bread crumbs, pomodoro, and mozzarella. Great for sharing, 14 oz. Served with choice of penne pasta or vegetables and potatoes.",
    price: 65,
    priceText: "$65.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    isPopular: false,
    sortOrder: 34,
  },
  {
    name: "Chicken & Artichokes",
    description: "Lemon and butter. Served with choice of penne pasta or vegetables and potatoes.",
    price: 29,
    priceText: "$29.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    isPopular: false,
    sortOrder: 35,
  },
  {
    name: "Atlantic Salmon",
    description:
      "Lemon butter or puttanesca. Served with choice of penne pasta or vegetables and potatoes.",
    price: 31,
    priceText: "$31.00",
    category: "seafood",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/32-atlantic-salmon.jpg",
    isPopular: false,
    sortOrder: 36,
  },
  {
    name: "Branzino",
    description: "Lemon and butter. Served with choice of penne pasta or vegetables and potatoes.",
    price: 42,
    priceText: "$42.00",
    category: "seafood",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/25-branzino.jpg",
    isPopular: true,
    sortOrder: 37,
  },
  {
    name: "Eggplant Parmigiana",
    description:
      "Mozzarella and pomodoro. Served with choice of penne pasta or vegetables and potatoes.",
    price: 28,
    priceText: "$28.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/33-eggplant-parmigiana.jpg",
    isPopular: false,
    sortOrder: 38,
  },
  {
    name: "Chicken Marsala",
    description:
      "Butter, marsala wine, and mushrooms. Served with choice of penne pasta or vegetables and potatoes.",
    price: 28,
    priceText: "$28.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/26-chicken-marsala.jpg",
    isPopular: false,
    sortOrder: 39,
  },
  {
    name: "Chicken Francese",
    description:
      "Lightly battered in egg, lemon, and butter. Served with choice of penne pasta or vegetables and potatoes.",
    price: 28,
    priceText: "$28.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/34-chicken-francese.jpg",
    isPopular: false,
    sortOrder: 40,
  },
  {
    name: "Spicy Sausage Penne alla Vodka",
    description: "Pomodoro, cream, red pepper, and Italian sausage.",
    price: 32,
    priceText: "$32.00",
    category: "mains",
    section: "dinner",
    originalSection: "Secondi Primi",
    isPopular: false,
    sortOrder: 41,
  },
  {
    name: "Frutti di Mare",
    description: "Linguini, pomodoro, clams, shrimp, scallops, and calamari.",
    price: 48,
    priceText: "$48.00",
    category: "seafood",
    section: "dinner",
    originalSection: "Secondi Primi",
    isPopular: false,
    sortOrder: 42,
  },
  // ── Sides ────────────────────────────────────────────────────────────────
  {
    name: "Mushrooms",
    description: "",
    price: 7,
    priceText: "$7.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 43,
  },
  {
    name: "Asparagus",
    description: "",
    price: 7,
    priceText: "$7.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 44,
  },
  {
    name: "Potato",
    description: "",
    price: 7,
    priceText: "$7.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 45,
  },
  {
    name: "Sautéed Spinach",
    description: "",
    price: 7,
    priceText: "$7.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 46,
  },
  {
    name: "Broccoli",
    description: "",
    price: 7,
    priceText: "$7.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 47,
  },
  {
    name: "Side of Penne alla Vodka",
    description: "",
    price: 9,
    priceText: "$9.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 48,
  },
  {
    name: "Side of Alfredo Pappardelle",
    description: "",
    price: 9,
    priceText: "$9.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 49,
  },
  {
    name: "Side of Cacio e Pepe",
    description: "",
    price: 9,
    priceText: "$9.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 50,
  },
  {
    name: "Side of Tortellini",
    description: "",
    price: 9,
    priceText: "$9.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    photoUrl: "/images/menu/roma/13-tortellini.jpg",
    isPopular: false,
    sortOrder: 51,
  },
  {
    name: "Side of Penne Bolognese",
    description: "",
    price: 9,
    priceText: "$9.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    photoUrl: "/images/menu/roma/12-penne-bolognese.jpg",
    isPopular: false,
    sortOrder: 52,
  },
  {
    name: "Side of Angel Hair with Butter",
    description: "",
    price: 9,
    priceText: "$9.00",
    category: "sides",
    section: "dinner",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 53,
  },
  // ── Desserts ─────────────────────────────────────────────────────────────
  {
    name: "House Made Cheesecake",
    description: "Housemade cheesecake.",
    price: 13,
    priceText: "$13.00",
    category: "desserts",
    section: "dinner",
    originalSection: "Desserts",
    photoUrl: "/images/menu/roma/27-house-made-cheesecake.jpg",
    isPopular: false,
    sortOrder: 54,
  },
  {
    name: "Tiramisu",
    description: "Housemade tiramisu.",
    price: 13,
    priceText: "$13.00",
    category: "desserts",
    section: "dinner",
    originalSection: "Desserts",
    photoUrl: "/images/menu/roma/28-tiramisu.jpg",
    isPopular: true,
    sortOrder: 55,
  },
  {
    name: "Affogato",
    description: "Gelato topped with espresso.",
    price: 10,
    priceText: "$10.00",
    category: "desserts",
    section: "dinner",
    originalSection: "Desserts",
    isPopular: false,
    sortOrder: 56,
  },
  {
    name: "Sorbet",
    description: "Mango, raspberry, or lemon.",
    price: 12,
    priceText: "$12.00",
    category: "desserts",
    section: "dinner",
    originalSection: "Desserts",
    isPopular: false,
    sortOrder: 57,
  },
  {
    name: "Locally Made Gelato",
    description: "Vanilla, chocolate, or pistachio.",
    price: 12,
    priceText: "$12.00",
    category: "desserts",
    section: "dinner",
    originalSection: "Desserts",
    photoUrl: "/images/menu/roma/29-locally-made-gelato.jpg",
    isPopular: false,
    sortOrder: 58,
  },
  // ── Catering ─────────────────────────────────────────────────────────────
  {
    name: "Bread & Butter",
    description: "Half serves 6-8; full serves 12-14. Add garlic: half $5 / full $10.",
    price: 15,
    priceText: "$15.00/Half",
    fullPrice: 30,
    fullPriceText: "$30.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/01-bread-and-butter.jpg",
    isPopular: false,
    sortOrder: 101,
  },
  {
    name: "Bruschetta",
    description: "Half serves 6-8; full serves 12-14.",
    price: 60,
    priceText: "$60.00/Half",
    fullPrice: 120,
    fullPriceText: "$120.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/03-bruschetta.jpg",
    isPopular: false,
    sortOrder: 102,
  },
  {
    name: "Warm Olives",
    description: "Half serves 6-8; full serves 12-14.",
    price: 40,
    priceText: "$40.00/Half",
    fullPrice: 80,
    fullPriceText: "$80.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/02-warm-olives.jpg",
    isPopular: false,
    sortOrder: 103,
  },
  {
    name: "Stuffed Artichokes",
    description: "Half serves 6-8; full serves 12-14.",
    price: 80,
    priceText: "$80.00/Half",
    fullPrice: 160,
    fullPriceText: "$160.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/36-stuffed-artichokes.jpg",
    isPopular: false,
    sortOrder: 104,
  },
  {
    name: "Roasted Red Peppers & Burrata",
    description: "Half serves 6-8; full serves 12-14.",
    price: 65,
    priceText: "$65.00/Half",
    fullPrice: 130,
    fullPriceText: "$130.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    isPopular: false,
    sortOrder: 105,
  },
  {
    name: "Meatballs with Marinara",
    description: "Half serves 6-9; full serves 12-14.",
    price: 55,
    priceText: "$55.00/Half",
    fullPrice: 110,
    fullPriceText: "$110.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/35-meatballs.jpg",
    isPopular: false,
    sortOrder: 106,
  },
  {
    name: "Shrimp Scampi",
    description: "Half tray / full tray.",
    price: 80,
    priceText: "$80.00/Half",
    fullPrice: 160,
    fullPriceText: "$160.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/06-shrimp-scampi.jpg",
    isPopular: false,
    sortOrder: 107,
  },
  {
    name: "Fried Calamari",
    description: "Half tray / full tray.",
    price: 80,
    priceText: "$80.00/Half",
    fullPrice: 160,
    fullPriceText: "$160.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/05-fried-calamari.jpg",
    isPopular: false,
    sortOrder: 108,
  },
  {
    name: "Crostini with Prosciutto",
    description: "Half tray / full tray.",
    price: 60,
    priceText: "$60.00/Half",
    fullPrice: 120,
    fullPriceText: "$120.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Antipasti",
    photoUrl: "/images/menu/roma/07-crostini.jpg",
    isPopular: false,
    sortOrder: 109,
  },
  {
    name: "Caesar Salad",
    description: "Half serves 6-8; full serves 12-14. Add garlic: half $5 / full $10.",
    price: 60,
    priceText: "$60.00/Half",
    fullPrice: 120,
    fullPriceText: "$120.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/caesar-salad.jpg",
    isPopular: false,
    sortOrder: 110,
  },
  {
    name: "Caprese Salad",
    description: "Half serves 6-8; full serves 12-14.",
    price: 65,
    priceText: "$65.00/Half",
    fullPrice: 130,
    fullPriceText: "$130.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/08-caprese-salad.jpg",
    isPopular: false,
    sortOrder: 111,
  },
  {
    name: "Avocado & Bacon Salad",
    description: "Half serves 6-8; full serves 12-14.",
    price: 65,
    priceText: "$65.00/Half",
    fullPrice: 130,
    fullPriceText: "$130.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/09-avocado-and-bacon-salad.jpg",
    isPopular: false,
    sortOrder: 112,
  },
  {
    name: "Grilled Vegetable Salad",
    description: "Half serves 6-8; full serves 12-14.",
    price: 65,
    priceText: "$65.00/Half",
    fullPrice: 130,
    fullPriceText: "$130.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/10-grilled-vegetable-salad.jpg",
    isPopular: false,
    sortOrder: 113,
  },
  {
    name: "Mixed Green Salad",
    description: "Half serves 6-8; full serves 12-14.",
    price: 60,
    priceText: "$60.00/Half",
    fullPrice: 120,
    fullPriceText: "$120.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Insalata",
    photoUrl: "/images/menu/roma/11-mixed-greens-salad.jpg",
    isPopular: false,
    sortOrder: 114,
  },
  {
    name: "Penne Bolognese",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/12-penne-bolognese.jpg",
    isPopular: false,
    sortOrder: 115,
  },
  {
    name: "Tortellini",
    description: "Half serves 6-8; full serves 12-14.",
    price: 90,
    priceText: "$90.00/Half",
    fullPrice: 180,
    fullPriceText: "$180.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/13-tortellini.jpg",
    isPopular: false,
    sortOrder: 116,
  },
  {
    name: "Gnocchi with Truffle Cream",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/14-gnocchi.jpg",
    isPopular: false,
    sortOrder: 117,
  },
  {
    name: "Meat Lasagna",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/16-meat-lasagna.jpg",
    isPopular: false,
    sortOrder: 118,
  },
  {
    name: "Spaghetti Cacio e Pepe",
    description: "Half serves 6-8; full serves 12-14.",
    price: 80,
    priceText: "$80.00/Half",
    fullPrice: 160,
    fullPriceText: "$160.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/17-spaghetti-cacio-e-pepe.jpg",
    isPopular: false,
    sortOrder: 119,
  },
  {
    name: "Pappardelle Alfredo",
    description: "Half serves 6-8; full serves 12-14. Add chicken: half $24 / full $48.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/18-pappardelle-alfredo.jpg",
    isPopular: false,
    sortOrder: 120,
  },
  {
    name: "Rigatoni Puttanesca",
    description: "Half serves 6-8; full serves 12-14.",
    price: 90,
    priceText: "$90.00/Half",
    fullPrice: 180,
    fullPriceText: "$180.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/19-rigatoni-puttanesca.jpg",
    isPopular: false,
    sortOrder: 121,
  },
  {
    name: "Spaghetti & Meatballs",
    description: "Half serves 6-8; full serves 12-14.",
    price: 90,
    priceText: "$90.00/Half",
    fullPrice: 180,
    fullPriceText: "$180.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/20-spaghetti-and-meatballs.jpg",
    isPopular: false,
    sortOrder: 122,
  },
  {
    name: "Shrimp Diablo over Pasta",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "La Pasta",
    photoUrl: "/images/menu/roma/21-shrimp-diablo.jpg",
    isPopular: false,
    sortOrder: 123,
  },
  {
    name: "Chicken Parmigiana",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/22-chicken-parmigiana.jpg",
    isPopular: false,
    sortOrder: 124,
  },
  {
    name: "Veal Parmigiana",
    description: "Half serves 6-8; full serves 12-14.",
    price: 160,
    priceText: "$160.00/Half",
    fullPrice: 320,
    fullPriceText: "$320.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    isPopular: false,
    sortOrder: 125,
  },
  {
    name: "Chicken Milanese",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/23-chicken-milanese.jpg",
    isPopular: false,
    sortOrder: 126,
  },
  {
    name: "Chicken Piccata",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/24-chicken-piccata.jpg",
    isPopular: false,
    sortOrder: 127,
  },
  {
    name: "Chicken Marsala",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/26-chicken-marsala.jpg",
    isPopular: false,
    sortOrder: 128,
  },
  {
    name: "Chicken Francese",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/34-chicken-francese.jpg",
    isPopular: false,
    sortOrder: 129,
  },
  {
    name: "Pork Chop",
    description: "Half serves 6-8; full serves 12-14.",
    price: 110,
    priceText: "$110.00/Half",
    fullPrice: 220,
    fullPriceText: "$220.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/31-duroc-pork-chops.jpg",
    isPopular: false,
    sortOrder: 130,
  },
  {
    name: "Salmon",
    description: "Half serves 6-8; full serves 12-14.",
    price: 120,
    priceText: "$120.00/Half",
    fullPrice: 240,
    fullPriceText: "$240.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/32-atlantic-salmon.jpg",
    isPopular: false,
    sortOrder: 131,
  },
  {
    name: "Eggplant Parmigiano",
    description: "Half serves 6-8; full serves 12-14.",
    price: 95,
    priceText: "$95.00/Half",
    fullPrice: 190,
    fullPriceText: "$190.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Secondi Primi",
    photoUrl: "/images/menu/roma/33-eggplant-parmigiana.jpg",
    isPopular: false,
    sortOrder: 132,
  },
  {
    name: "Asparagus",
    description: "Half serves 6-8; full serves 12-14.",
    price: 30,
    priceText: "$30.00/Half",
    fullPrice: 60,
    fullPriceText: "$60.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 133,
  },
  {
    name: "Spinach",
    description: "Half serves 6-8; full serves 12-14.",
    price: 30,
    priceText: "$30.00/Half",
    fullPrice: 60,
    fullPriceText: "$60.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 134,
  },
  {
    name: "Mushrooms",
    description: "Half serves 6-8; full serves 12-14.",
    price: 30,
    priceText: "$30.00/Half",
    fullPrice: 60,
    fullPriceText: "$60.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 135,
  },
  {
    name: "Potato",
    description: "Half serves 6-8; full serves 12-14.",
    price: 30,
    priceText: "$30.00/Half",
    fullPrice: 60,
    fullPriceText: "$60.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 136,
  },
  {
    name: "Penne",
    description: "Half serves 6-8; full serves 12-14.",
    price: 30,
    priceText: "$30.00/Half",
    fullPrice: 60,
    fullPriceText: "$60.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Sides",
    isPopular: false,
    sortOrder: 137,
  },
  {
    name: "Tiramisu",
    description: "Half serves 6; full serves 12.",
    price: 50,
    priceText: "$50.00/Half",
    fullPrice: 100,
    fullPriceText: "$100.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Desserts",
    photoUrl: "/images/menu/roma/28-tiramisu.jpg",
    isPopular: false,
    sortOrder: 138,
  },
  {
    name: "Cheesecake",
    description: "Half serves 6; full serves 12.",
    price: 50,
    priceText: "$50.00/Half",
    fullPrice: 100,
    fullPriceText: "$100.00/Full",
    category: "catering_trays",
    section: "catering",
    originalSection: "Desserts",
    photoUrl: "/images/menu/roma/27-house-made-cheesecake.jpg",
    isPopular: false,
    sortOrder: 139,
  },
  // ── Cocktails ────────────────────────────────────────────────────────────
  {
    name: "Tito's Dirty Martini",
    description: "Vodka, dry vermouth, olive juice, and olives.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 201,
  },
  {
    name: "Sapphire Gin Martini",
    description: "Gin, dry vermouth, olive juice, and olives.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 202,
  },
  {
    name: "Whistle Pig Manhattan",
    description: "Whiskey, sweet vermouth, and cherries.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 203,
  },
  {
    name: "Espresso Martini",
    description: "Ketel One, Kahlua, Baileys, and espresso.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 204,
  },
  {
    name: "Cosmopolitan",
    description: "Vodka, triple sec, cranberry juice, and lime juice.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 205,
  },
  {
    name: "Cucumber Martini",
    description: "Vodka, cucumber, basil, elderflower liqueur, and lemon juice.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 206,
  },
  {
    name: "Limoncello Martini",
    description: "Vodka, limoncello, simple syrup, and lemon juice.",
    price: 24,
    priceText: "$24.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 207,
  },
  {
    name: "Negroni",
    description: "Gin, Campari, and sweet vermouth.",
    price: 18,
    priceText: "$18.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 208,
  },
  {
    name: "Smoked Old Fashioned",
    description: "Angel's Envy, simple syrup, bitters, cherries, and orange peel.",
    price: 18,
    priceText: "$18.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 209,
  },
  {
    name: "Cadillac Margarita",
    description: "Tequila, triple sec, lime, and Grand Marnier.",
    price: 18,
    priceText: "$18.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 210,
  },
  {
    name: "Carajillo",
    description: "Licor 43 and espresso.",
    price: 18,
    priceText: "$18.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 211,
  },
  {
    name: "El Cristiano Paloma Spritz",
    description: "Tequila, Aperol, lime, soda, and prosecco.",
    price: 18,
    priceText: "$18.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 212,
  },
  {
    name: "El Cristiano Smoked Old Fashioned",
    description: "Tequila, simple syrup, bitters, cherries, and orange peel.",
    price: 18,
    priceText: "$18.00",
    category: "cocktails",
    section: "drinks",
    originalSection: "Cocktails",
    isPopular: false,
    sortOrder: 213,
  },
  // ── White Wines (6oz/9oz/bottle) ─────────────────────────────────────────
  {
    name: "Casa Lunardi Pinot Grigio",
    description: "Veneto, Italy.",
    price: 13,
    priceText: "6oz $13 / 9oz $19 / Bottle $52",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 221,
  },
  {
    name: "Daou Sauvignon Blanc",
    description: "Paso Robles.",
    price: 13,
    priceText: "6oz $13 / 9oz $19 / Bottle $52",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 222,
  },
  {
    name: "Del Dotto Sauvignon Blanc",
    description: "Napa.",
    price: 20,
    priceText: "6oz $20 / 9oz $30 / Bottle $80",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 223,
  },
  {
    name: "Sea Sun Chardonnay",
    description: "Central Coast.",
    price: 12,
    priceText: "6oz $12 / 9oz $18 / Bottle $48",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 224,
  },
  {
    name: "Rombauer Chardonnay",
    description: "Carneros.",
    price: 25,
    priceText: "6oz $25 / 9oz $37 / Bottle $100",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 225,
  },
  {
    name: "Cordero San Giorgio Exergia Moscato d'Asti",
    description: "Piedmont, Italy.",
    price: 13,
    priceText: "6oz $13 / 9oz $19 / Bottle $52",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 226,
  },
  {
    name: "Maddalena Riesling",
    description: "Monterey.",
    price: 12,
    priceText: "6oz $12 / 9oz $18 / Bottle $48",
    category: "wine",
    section: "drinks",
    originalSection: "White Wines",
    isPopular: false,
    sortOrder: 227,
  },
  // ── Rosé (6oz/9oz/bottle) ────────────────────────────────────────────────
  {
    name: "Chateau d'Esclans Whispering Angel Rosé",
    description: "France.",
    price: 15,
    priceText: "6oz $15 / 9oz $27 / Bottle $60",
    category: "wine",
    section: "drinks",
    originalSection: "Rosé",
    isPopular: false,
    sortOrder: 229,
  },
  // ── Sparkling Wine ───────────────────────────────────────────────────────
  {
    name: "Le Grand Courtage Blanc de Blancs Brut",
    description: "France. 187ml split.",
    price: 18,
    priceText: "$18.00 (187ml split)",
    category: "wine",
    section: "drinks",
    originalSection: "Sparkling Wine",
    isPopular: false,
    sortOrder: 231,
  },
  {
    name: "Scarpetta Prosecco",
    description: "Friuli-Venezia Giulia, Italy.",
    price: 13,
    priceText: "6oz $13 / 9oz $19 / Bottle $52",
    category: "wine",
    section: "drinks",
    originalSection: "Sparkling Wine",
    isPopular: false,
    sortOrder: 232,
  },
  // ── Red Wines (6oz/9oz/bottle) ───────────────────────────────────────────
  {
    name: "Castello di Monsanto Chianti Classico Riserva",
    description: "Tuscany, Italy.",
    price: 20,
    priceText: "6oz $20 / 9oz $31 / Bottle $85",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 241,
  },
  {
    name: "Ruffino Riserva Ducale Chianti",
    description: "Tuscany, Italy.",
    price: 18,
    priceText: "6oz $18 / 9oz $27 / Bottle $72",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 242,
  },
  {
    name: "Antinori Villa Toscana Red",
    description: "Tuscany, Italy.",
    price: 18,
    priceText: "6oz $18 / 9oz $27 / Bottle $72",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 243,
  },
  {
    name: "Daniele Conterno Nebbiolo d'Alba",
    description: "Piedmont, Italy.",
    price: 20,
    priceText: "6oz $20 / 9oz $30 / Bottle $80",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 244,
  },
  {
    name: "Famiglia Rivetti Barolo",
    description: "Piedmont, Italy.",
    price: 30,
    priceText: "6oz $30 / 9oz $45 / Bottle $120",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 245,
  },
  {
    name: "Sea Sun Pinot Noir",
    description: "Monterey.",
    price: 12,
    priceText: "6oz $12 / 9oz $18 / Bottle $48",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 246,
  },
  {
    name: "Decoy Merlot",
    description: "Sonoma.",
    price: 12,
    priceText: "6oz $12 / 9oz $18 / Bottle $48",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 247,
  },
  {
    name: "Justin Isosceles Cabernet Blend",
    description: "Paso Robles.",
    price: 30,
    priceText: "6oz $30 / 9oz $45 / Bottle $120",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 248,
  },
  {
    name: "Robert Mondavi Cabernet Sauvignon",
    description: "Napa.",
    price: 12,
    priceText: "6oz $12 / 9oz $18 / Bottle $48",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 249,
  },
  {
    name: "Justin Cabernet Sauvignon",
    description: "Paso Robles.",
    price: 18,
    priceText: "6oz $18 / 9oz $27 / Bottle $72",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 250,
  },
  {
    name: "Caymus California Cabernet Sauvignon",
    description: "Napa.",
    price: 25,
    priceText: "6oz $25 / 9oz $37 / Bottle $100",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 251,
  },
  {
    name: "Stag's Leap Cabernet Sauvignon",
    description: "Napa.",
    price: 30,
    priceText: "6oz $30 / 9oz $45 / Bottle $120",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 252,
  },
  {
    name: "Robert Foley Claret",
    description: "Napa.",
    price: 120,
    priceText: "Bottle $120",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 253,
  },
  {
    name: "Del Dotto Cave Blend",
    description: "Napa.",
    price: 130,
    priceText: "Bottle $130",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 254,
  },
  {
    name: "Twomey Pinot Noir",
    description: "Santa Barbara.",
    price: 130,
    priceText: "Bottle $130",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 255,
  },
  {
    name: "Caymus Napa Valley Cabernet Sauvignon",
    description: "Napa.",
    price: 140,
    priceText: "Bottle $140",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 256,
  },
  {
    name: "Silver Oak Cabernet Sauvignon",
    description: "Alexander Valley.",
    price: 140,
    priceText: "Bottle $140",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 257,
  },
  {
    name: "Daniele Conterno Barolo Bussia Riserva",
    description: "Piedmont, Italy.",
    price: 150,
    priceText: "Bottle $150",
    category: "wine",
    section: "drinks",
    originalSection: "Red Wines",
    isPopular: false,
    sortOrder: 258,
  },
  // ── Beers ────────────────────────────────────────────────────────────────
  {
    name: "Stella Artois",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 261,
  },
  {
    name: "Peroni",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 262,
  },
  {
    name: "Coors Light",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 263,
  },
  {
    name: "Lagunitas",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 264,
  },
  {
    name: "Blue Moon",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 265,
  },
  {
    name: "Nutrl Watermelon",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 266,
  },
  {
    name: "Nutrl Pineapple",
    description: "Bottle.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 267,
  },
  {
    name: "Peroni Non-Alcoholic",
    description: "Bottle. Non-alcoholic.",
    price: 7.5,
    priceText: "$7.50",
    category: "beer",
    section: "drinks",
    originalSection: "Beers",
    isPopular: false,
    sortOrder: 268,
  },
  // ── Beverages ────────────────────────────────────────────────────────────
  {
    name: "Coke",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 271,
  },
  {
    name: "Diet Coke",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 272,
  },
  {
    name: "Sprite",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 273,
  },
  {
    name: "Dr. Pepper",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 274,
  },
  {
    name: "Lemonade",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 275,
  },
  {
    name: "Iced Tea",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 276,
  },
  {
    name: "Ghost Energy Orange Cream",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 277,
  },
  {
    name: "Panna",
    description: "Still water.",
    price: 9,
    priceText: "$9.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 278,
  },
  {
    name: "San Pellegrino",
    description: "Sparkling water.",
    price: 9,
    priceText: "$9.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 279,
  },
  {
    name: "Coffee",
    description: "",
    price: 5,
    priceText: "$5.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 280,
  },
  {
    name: "Espresso",
    description: "",
    price: 6,
    priceText: "$6.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 281,
  },
  {
    name: "Cappuccino",
    description: "",
    price: 6,
    priceText: "$6.00",
    category: "beverages",
    section: "drinks",
    originalSection: "Beverages",
    isPopular: false,
    sortOrder: 282,
  },
];
